import * as CognitoIdentity from 'amazon-cognito-identity-js';
import * as jwt from 'jsonwebtoken';
import { ChangePassword, CodeConfirmationModel } from '../models/user/user';
import { CognitoUserAttributeName, createCognitoUserAttribute } from '../utils/cognito';
import { CognitoAuth } from 'amazon-cognito-auth-js';
import { appWebDomain, clientId, redirectURI, region, userPoolId } from '../utils/constants';
import { LogManager } from '../utils/LogManager';
import { Nullable } from '../utils/types';
import { beautifyMessageIfError, bindObjectMethods } from '../utils/utilFunctions';
import { createDefaultApi } from '../utils/createApi';
import { Attributes } from 'fs-api-client';

export class User {
    constructor(
        readonly email: string,
        readonly password: string,
        readonly referralCode?: string,
        readonly utmData?: string
    ) {}
}

const logManager = new LogManager('authentication');

const POOL_DATA = {
    UserPoolId: userPoolId,
    ClientId: clientId,
};

const ERROR_MESSAGES = {
    USER_NOT_FOUND: 'User not found.',
    USER_SESSION_NOT_FOUND: 'User session not found.',
    ID_TOKEN_NOT_FOUND: 'Id token not found.',
    USER_SESSION_INVALID: 'User session is not valid.',
    RESULT_NOT_DEFINED: 'Result is not defined.'
};

const LOCAL_STORAGE_VARIABLES = {
    SOCIAL_ACCOUNT_TYPE_KEY: 'socialAccountType',
    IS_SOCIAL_ACCOUNT_KEY: 'isSocialAccount',
    WAS_AUTHENTICATED: 'wasAuthenticated',
    LAST_KNOWN_USER: 'lastKnownUser',
    LAST_KNOWN_USER_ID: 'lastKnownUserId',
};

interface LocalStorageStateHolder {
    deserializeFromLocalStorage: () => void;
    serializeToLocalStorage: () => void;
}

export interface DecodedTokenUserIdentity {
    userId: string;
    providerName: string;
    providerType: string;
    issuer: any;
    primary: string;
    dateCreated: string;
}

export interface DecodedToken {
    payload: {
        at_hash: string;
        sub: string;
        'cognito:groups': Array<string>;
        email_verified: boolean;
        iss: string;
        'cognito:username': string;
        given_name: string;
        aud: string;
        identities: Array<DecodedTokenUserIdentity>;
        token_use: string;
        auth_time: number;
        name: string;
        exp: number;
        iat: number;
        family_name: string;
        email: string;
    };
}

class AuthenticationError extends Error {
    public readonly isAuthenticationError: boolean = true;
}

interface AuthenticationService {
    getDecodedIdToken(): Promise<DecodedToken>;
}

class WebportalAuthenticationService implements LocalStorageStateHolder, AuthenticationService {
    private cognitoAuth: CognitoAuth;
    private userPool: CognitoIdentity.CognitoUserPool;
    private lastIdentityProvider: string;

    private isSocialAccount: Nullable<boolean>;
    private socialAccountType: Nullable<string>;
    private wasAuthenticated: Nullable<boolean>;
    private lastKnownUser: Nullable<string>;
    private lastKnownUserId: Nullable<string>;

    private onSocialLoginSuccess: Function | null = null;
    private onSocialLoginFailure: Function | null = null;

    private customErrorHandler: () => void;
    private onLoggedInCallback: () => void;

    constructor() {
        this.userPool = new CognitoIdentity.CognitoUserPool(POOL_DATA);
        this.initializeCognitoAuth();

        this.deserializeFromLocalStorage();

        bindObjectMethods(this, WebportalAuthenticationService.prototype);
    }

    getErrorHandler(): () => void {
        return this.customErrorHandler;
    }

    setErrorHandler(customErrorHandler: () => void): void {
        this.customErrorHandler = customErrorHandler;
    }

    setIsSocialAccount(isSocialAccount: Nullable<boolean>): void {
        this.isSocialAccount = isSocialAccount;
        this.serializeToLocalStorage();
    }

    setSocialAccountType(socialAccountType: Nullable<string>): void {
        this.socialAccountType = socialAccountType;
        this.serializeToLocalStorage();
    }

    setWasAuthenticated(wasAuthenticated: Nullable<boolean>): void {
        this.wasAuthenticated = wasAuthenticated;
        this.serializeToLocalStorage();
    }

    setLastKnownUser(lastKnownUser: Nullable<string>): void {
        this.lastKnownUser = lastKnownUser;
        this.serializeToLocalStorage();
    }

    setLastKnownUserId(lastKnownUserId: Nullable<string>): void {
        this.lastKnownUserId = lastKnownUserId;
        this.serializeToLocalStorage();
    }

    getIsSocialAccount(): boolean {
        return !!this.isSocialAccount;
    }

    getSocialAccountType(): Nullable<string> {
        return this.socialAccountType;
    }

    getWasAuthenticated(): boolean {
        return !!this.wasAuthenticated;
    }

    getLastKnownUser(): Nullable<string> {
        return this.lastKnownUser;
    }

    getLastKnownUserId(): Nullable<string> {
        return this.lastKnownUserId;
    }

    setSocialLoginCallbacks(
        onSocialLoginSuccess: () => void,
        onSocialLoginFailure: (error: any) => void
    ): void {
        this.onSocialLoginSuccess = onSocialLoginSuccess;
        this.onSocialLoginFailure = onSocialLoginFailure;
    }

    async getUserData(): Promise<CognitoIdentity.UserData> {
        const currentUser = await this.getCurrentUser();
        this.handleNullValue(currentUser, ERROR_MESSAGES.USER_NOT_FOUND);

        return new Promise((resolve, reject) => {
            currentUser.getUserData((error: Error, data: any) => {
                if (error) {
                    reject(error);
                } else {
                    resolve(data);
                }
            });
        });
    }

    getIdToken(): Promise<string> {
        return this.getIdTokenInner(false) as Promise<string>;
    }

    getDecodedIdToken(): Promise<DecodedToken> {
        return this.getIdTokenInner(true) as Promise<DecodedToken>;
    }

    async getCurrentUserEmail(): Promise<string> {
        const idToken = await this.getDecodedIdToken();
        this.handleNullValue(idToken, ERROR_MESSAGES.ID_TOKEN_NOT_FOUND);
        return idToken.payload.email;
    }

    setOnLoggedInCallback(value: () => void) {
        this.onLoggedInCallback = value;
    }

    async login(provider: string, queryParams?: any): Promise<void>;
    async login(user: User): Promise<void>;
    async login(argument: string | User, queryParams?: any): Promise<void> {
        if (typeof argument === 'string') {
            this.socialLogin(argument);

            if (queryParams) {
                localStorage.setItem('marketingData', queryParams);
            }

            return Promise.resolve();
        } else {
            await this.credentialsLogin(argument);
            try {
                await this.trackLastKnownUser();
            } catch (error) {
                logManager.error('Username/password login track last known user failed');
                logManager.error(error);
            }

            if (this.onLoggedInCallback) {
                this.onLoggedInCallback();
            }
        }

    }

    register(user: User): Promise<string> {
        const _this = this;
        return new Promise((resolve, reject) => {
            const attributes = this.createCognitoUserAttributes(user);
            this.userPool.signUp(
                user.email,
                user.password,
                attributes,
                [],
                function (error: Error, result: CognitoIdentity.ISignUpResult) {
                    if (error) {
                        reject(error);
                        return;
                    }

                    _this.handleNullValue(result, ERROR_MESSAGES.RESULT_NOT_DEFINED);
                    _this.handleNullValue(result.user, ERROR_MESSAGES.RESULT_NOT_DEFINED);
                    resolve(result.user.getUsername());
                }
            );
        });
    }

    resendCode(username: string): Promise<any> {
        const cognitoUser = this.createCognitoUser(username);
        return new Promise((resolve, reject) => {
            cognitoUser.resendConfirmationCode((error: any, result: any) => {
                if (error) {
                    reject(error);
                    return;
                }
                resolve(result);
            });
        });
    }

    confirmCode(confirmation: CodeConfirmationModel): Promise<any> {
        const cognitoUser = this.createCognitoUser(confirmation.email);
        return new Promise((resolve, reject) => {
            cognitoUser.confirmRegistration(confirmation.code, true, (error: any, result: any) => {
                if (error) {
                    reject(error);
                    return;
                }
                resolve(result);
            });
        });
    }

    async signOut(): Promise<void> {
        return this.signOutInner();
    }

    async changePassword(changePasswordData: ChangePassword): Promise<any> {
        const cognitoUser = await this.getCurrentUser();
        if (!cognitoUser) {
            return;
        }
        return new Promise((resolve, reject) => {
            cognitoUser.changePassword(
                changePasswordData.oldPassword!,
                changePasswordData.newPassword!,
                (error: any, result: any) => {
                            if (error) {
                                reject(error);
                            }
                            resolve(result);
                        });
            }
        );
    }

    resetPassword(
        email: string,
        inputVerificationCode: () => void = () => {}
    ): Promise<void> {
        const cognitoUser = this.createCognitoUser(email);
        return new Promise((resolve, reject) => {
            cognitoUser.forgotPassword({
                onSuccess: () => {
                    resolve();
                },
                onFailure: (error) => {
                    reject(error);
                },
                inputVerificationCode: () => {
                    resolve();
                    inputVerificationCode();
                }
            });
        });
    }

    confirmNewPassword(
        user: User,
        verificationCode: string,
    ): Promise<void> {
        const cognitoUser = this.createCognitoUser(user.email);
        return new Promise((resolve, reject) => {
            cognitoUser.confirmPassword(
                verificationCode,
                user.password,
                {
                    onSuccess: () => {
                        resolve();
                    },
                    onFailure: (error) => {
                        reject(error);
                    }
                }
            );
        });
    }

    async getUserAttributes(): Promise<Attributes> {
        const currentUser = await this.getCurrentUser();
        this.handleNullValue(currentUser, ERROR_MESSAGES.USER_NOT_FOUND);

        const defaultApi = await createDefaultApi();
        return (await defaultApi.getAttributes()).body;
    }

    async updateAttributes(
        attributes: Attributes
    ): Promise<void> {
        const currentUser = await this.getCurrentUser();
        this.handleNullValue(currentUser, ERROR_MESSAGES.USER_NOT_FOUND);
        if (!currentUser) { return; }

        const defaultApi = await createDefaultApi();
        const updateAttributes = { ...attributes };
        await defaultApi.updateAttributes(updateAttributes);
    }

    async isAuthenticated(): Promise<boolean> {
        try {
            const currentUser = await this.getCurrentUser();
            if (!currentUser) { return false; }
            return this.userSessionIsValid(currentUser);
        } catch (error) {
            return false;
        }
    }

    parseCognitoWebResponse(): void {
        this.cognitoAuth.parseCognitoWebResponse(window.location.href);
    }

    async retrySocialLogin(): Promise<void> {
        await this.signOutInner();
        this.initializeCognitoAuth();
        await this.login(this.lastIdentityProvider);
    }

    serializeToLocalStorage(): void {
        const serializeNullableVariable = (key: string, value: any) => {
            if (value) {
                localStorage.setItem(key, value.toString());
            } else {
                localStorage.removeItem(key);
            }
        };
        serializeNullableVariable(
            LOCAL_STORAGE_VARIABLES.IS_SOCIAL_ACCOUNT_KEY,
            this.isSocialAccount
        );
        serializeNullableVariable(
            LOCAL_STORAGE_VARIABLES.SOCIAL_ACCOUNT_TYPE_KEY,
            this.socialAccountType
        );
        serializeNullableVariable(
            LOCAL_STORAGE_VARIABLES.WAS_AUTHENTICATED,
            this.wasAuthenticated
        );
        serializeNullableVariable(
            LOCAL_STORAGE_VARIABLES.LAST_KNOWN_USER,
            this.lastKnownUser
        );
        serializeNullableVariable(
            LOCAL_STORAGE_VARIABLES.LAST_KNOWN_USER_ID,
            this.lastKnownUserId
        );
    }

    deserializeFromLocalStorage(): void {
        this.isSocialAccount = localStorage.getItem(
            LOCAL_STORAGE_VARIABLES.IS_SOCIAL_ACCOUNT_KEY
        ) === true.toString().toLowerCase();
        this.wasAuthenticated = localStorage.getItem(
            LOCAL_STORAGE_VARIABLES.WAS_AUTHENTICATED
        ) === true.toString().toLowerCase();
        this.socialAccountType = localStorage.getItem(
            LOCAL_STORAGE_VARIABLES.SOCIAL_ACCOUNT_TYPE_KEY
        );
        this.lastKnownUser = localStorage.getItem(
            LOCAL_STORAGE_VARIABLES.LAST_KNOWN_USER
        );
        this.lastKnownUserId = localStorage.getItem(
            LOCAL_STORAGE_VARIABLES.LAST_KNOWN_USER_ID
        );
    }

    protected async getUserSession(
        currentUser: CognitoIdentity.CognitoUser
    ): Promise<CognitoIdentity.CognitoUserSession> {
        this.handleNullValue(currentUser, ERROR_MESSAGES.USER_NOT_FOUND);
        return new Promise((resolve, reject) => {
            currentUser.getSession((error: Error, session: CognitoIdentity.CognitoUserSession) => {
                if (error) {
                    reject(error);
                    return;
                }

                resolve(session);
            });
        });
    }

    protected async userSessionIsValid(
        currentUser: CognitoIdentity.CognitoUser
    ): Promise<boolean> {
        if (!currentUser) { return false; }

        const userSession = await this.getUserSession(currentUser);
        this.handleNullValue(userSession, ERROR_MESSAGES.USER_SESSION_INVALID);
        if (userSession && userSession.isValid()) {
            if (this.shouldRefreshSession(userSession)) {
                await this.refreshSession(currentUser, userSession);
            }
            this.setWasAuthenticated(true);
            return true;
        } else {
            this.setWasAuthenticated(false);
            return false;
        }
    }

    protected async getCurrentUser(): Promise<CognitoIdentity.CognitoUser> {
        const currentUser = this.userPool.getCurrentUser() as CognitoIdentity.CognitoUser;
        this.handleNullValue(currentUser, ERROR_MESSAGES.USER_NOT_FOUND);

        const userSessionIsValid = await this.userSessionIsValid(currentUser);
        if (userSessionIsValid) {
            this.setWasAuthenticated(true);
        } else {
            this.handleNullValue(null, ERROR_MESSAGES.USER_SESSION_INVALID);
        }

        return currentUser;
    }

    private shouldRefreshSession(userSession: CognitoIdentity.CognitoUserSession): boolean {
        const accessToken = userSession.getAccessToken();
        const accessTokenExpiration = accessToken.getExpiration();
        const now = Math.floor(Date.now() / 1000);

        const expirationRemainingMinutes = Math.floor((accessTokenExpiration - now) / 60);
        // Avoiding timing issues with race conditions when expired tokens are not refreshed on time.
        if (expirationRemainingMinutes <= 5) { return true; }

        return false;
    }

    private async refreshSession(
        currentUser: CognitoIdentity.CognitoUser,
        userSession: CognitoIdentity.CognitoUserSession
    ): Promise<void> {
        logManager.log('Refreshing session...');
        return new Promise((resolve, reject) => {
            currentUser.refreshSession(
                userSession.getRefreshToken(),
                (error, session) => {
                    if (error) {
                        reject(error);
                        return;
                    }
                    resolve();
                }
            );
        });
    }

    private async getIdTokenInner(
        decode: boolean
    ): Promise<DecodedToken | string> {
        const currentUser = await this.getCurrentUser();
        this.handleNullValue(currentUser, ERROR_MESSAGES.USER_NOT_FOUND);
        const userSession = await this.getUserSession(currentUser);
        this.handleNullValue(userSession, ERROR_MESSAGES.USER_SESSION_NOT_FOUND);

        const token = userSession.getIdToken().getJwtToken();

        if (!decode) { return token; }

        const decodedToken = jwt.decode(token, { complete: true });
        this.handleNullValue(decodedToken, ERROR_MESSAGES.ID_TOKEN_NOT_FOUND);
        return decodedToken as unknown as DecodedToken;
    }

    private async signOutInner(): Promise<void> {
        this.setIsSocialAccount(null);
        this.setSocialAccountType(null);
        this.setWasAuthenticated(null);

        this.cognitoAuth.signOut();
    }

    private credentialsLogin(user: User): Promise<void> {
        const authenticationDetails = new CognitoIdentity.AuthenticationDetails({
            Username: user.email,
            Password: user.password
        });
        const cognitoUser = this.createCognitoUser(user.email);
        const _this = this;
        return new Promise((resolve, reject) => {
            cognitoUser.authenticateUser(authenticationDetails, {
                onSuccess: (result: any) => {
                    _this.setWasAuthenticated(true);
                    resolve();
                },
                onFailure: (error: any) => {
                    _this.setWasAuthenticated(false);
                    reject(error);
                }
            });
        });
    }

    private socialLogin(provider?: string): void {
        this.initializeCognitoAuth(provider);
        this.cognitoAuth.getSession();
    }

    private createCognitoUserAttributes(user: User): CognitoIdentity.CognitoUserAttribute[] {
        const cognitoUserAttributes: CognitoIdentity.CognitoUserAttribute[] = [];
        cognitoUserAttributes.push(createCognitoUserAttribute(CognitoUserAttributeName.Email, user.email));

        if (user.referralCode) {
            cognitoUserAttributes.push(createCognitoUserAttribute(
                CognitoUserAttributeName.PartnerSource,
                user.referralCode)
            );
        }

        if (user.utmData) {
            cognitoUserAttributes.push(createCognitoUserAttribute(
                CognitoUserAttributeName.UtmData,
                user.utmData)
            );
        }

        return cognitoUserAttributes;
    }

    private createCognitoUser(username: string): CognitoIdentity.CognitoUser {
        return new CognitoIdentity.CognitoUser({
            Username: username,
            Pool: this.userPool
        });
    }

    private initializeCognitoAuth(identityProvider?: string): void {
        if (identityProvider) {
            this.lastIdentityProvider = identityProvider;
        }

        const authData = {
            ClientId: clientId,
            AppWebDomain: appWebDomain + '.auth.' + region + '.amazoncognito.com',
            TokenScopesArray: ['openid', 'email', 'profile', 'phone', 'aws.cognito.signin.user.admin'],
            RedirectUriSignIn: `${redirectURI}/redirect`,
            RedirectUriSignOut: `${redirectURI}/login`,
            IdentityProvider: identityProvider || this.getSocialAccountType() || undefined,
            UserPoolId: userPoolId,
            AdvancedSecurityDataCollectionFlag: false
        };

        const cognitoAuth = new CognitoAuth(authData);
        if (identityProvider) {
            this.setSocialAccountType(identityProvider);
        }

        const getSocialAccount = () => {
            return this.getSocialAccountType() || 'social account';
        };

        cognitoAuth.userhandler = {
            onSuccess: async (result: any) => {
                this.setIsSocialAccount(true);
                this.setWasAuthenticated(true);

                try {
                    await this.trackLastKnownUser();
                    logManager.log(`Login succeeded for ${this.getLastKnownUser()} (${getSocialAccount()})`, true);
                } catch (error) {
                    logManager.log(`Login succeeded for unknown user (${getSocialAccount()})`, true);
                    logManager.error(error);
                }

                if (this.onSocialLoginSuccess) {
                    this.onSocialLoginSuccess();
                }

                if (this.onLoggedInCallback) {
                    this.onLoggedInCallback();
                }
            },
            onFailure: (error: any) => {
                this.setWasAuthenticated(false);
                logManager.error(`Login failed for ${this.getLastKnownUser() || '(unknown)'} (${getSocialAccount()})
${beautifyMessageIfError(error)}`);
                if (this.onSocialLoginFailure) {
                    this.onSocialLoginFailure(error);
                }
            }
        };

        cognitoAuth.useCodeGrantFlow();

        this.cognitoAuth = cognitoAuth;
    }

    private async trackLastKnownUser(): Promise<void> {
        // This needs to be refactored
        const userAttributes = await this.getUserAttributes();
        if (userAttributes) {
            const userEmail = await this.getCurrentUserEmail();
            if (userEmail) {
                this.setLastKnownUser(userEmail);
                this.setLastKnownUserId(userEmail);
            }
        }
    }

    private handleNullValue(target: any, message: string): void {
        const targetIsNull = !target && (typeof target !== 'boolean');

        if (targetIsNull) {
            this.setWasAuthenticated(false);
            throw new AuthenticationError(`Expected authentication error: ${message}`);
        }
    }

}

const authenticationService = new WebportalAuthenticationService();

authenticationService.setErrorHandler(() => {
    logManager.log('Not authenticated. Signing out and redirecting to login...');
    authenticationService.signOut();
    window.location.href = 'login';
});

export { authenticationService, AuthenticationService };



// WEBPACK FOOTER //
// ./src/webportal/services/authentication.ts